import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import clientMemoryApi from '../services/clientMemoryApi';
import { useAuth } from '../components/auth/AuthWrapper';
import { track } from '../utils/analytics';

const MemoryContext = createContext(null);

// A memory write that did not land is logged and sent to PostHog, never
// swallowed: a lost suburb-search memory means that suburb never reaches the
// nightly watch list, and until 2026-10-27 nothing recorded that it happened.
function reportMemoryWriteFailure(surface, memory, reason, detail) {
  console.warn(`Memory write failed (${surface}): ${reason}`, detail ?? '');
  try {
    track.memoryWriteFailed(surface, {
      reason,
      category: memory?.category ?? null,
      memory_type: memory?.memory_type ?? null,
      detail: detail ? String(detail).slice(0, 160) : null,
    });
  } catch (error) {
    console.warn('memory_write_failed event not sent:', error);
  }
}

export const useMemory = () => {
  const context = useContext(MemoryContext);
  if (!context) {
    return {
      clientProfile: null,
      memories: [],
      isLoaded: false,
      getRelevant: async () => [],
      syncContext: async () => {},
      addMemory: async () => {},
      deleteMemory: async () => {},
    };
  }
  return context;
};

export const MemoryProvider = ({ children }) => {
  const [clientProfile, setClientProfile] = useState(null);
  const [memories, setMemories] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const syncedRef = useRef(false);

  // Phase 1 single-source-of-truth: read userId from the authenticated context
  // (Clerk in prod, DevAuthProvider in dev), NOT from getEffectiveUserId()
  // which silently chains into DEFAULT_USER_ID and trips the auth-gap warning.
  const { user } = useAuth();
  const userId = user?.userId || null;

  // Sync localStorage data to memory backend once userId is available (Clerk
  // has a loading window where userId is null; we wait for the real value
  // rather than syncing under demo-user and overwriting later).
  useEffect(() => {
    if (!userId) return;
    if (syncedRef.current) return;
    syncedRef.current = true;

    const doSync = async () => {
      try {
        const stored = localStorage.getItem('heyward_portfolio_data');
        if (stored) {
          const parsed = JSON.parse(stored);
          await clientMemoryApi.syncContext(userId, parsed);
        }

        const profile = await clientMemoryApi.getProfile(userId);
        if (profile?.success !== false) {
          setClientProfile(profile);
        }
        setIsLoaded(true);
      } catch (error) {
        console.warn('Memory sync failed (non-critical):', error);
        setIsLoaded(true);
      }
    };

    doSync();
  }, [userId]);

  const getRelevant = useCallback(async (context, limit = 5) => {
    if (!userId) return [];  // not signed in yet — no-op
    try {
      const result = await clientMemoryApi.getRelevant(userId, context, limit);
      return result?.data || [];
    } catch {
      return [];
    }
  }, [userId]);

  const syncContextManual = useCallback(async () => {
    if (!userId) return;
    try {
      const stored = localStorage.getItem('heyward_portfolio_data');
      if (stored) {
        const parsed = JSON.parse(stored);
        const result = await clientMemoryApi.syncContext(userId, parsed);
        if (result?.data) {
          const profile = await clientMemoryApi.getProfile(userId);
          if (profile) setClientProfile(profile);
        }
      }
    } catch (error) {
      console.warn('Manual sync failed:', error);
    }
  }, [userId]);

  // surface names the caller for the failure event ('suburb_search').
  const addMemory = useCallback(async (memory, { surface = 'unknown' } = {}) => {
    if (!userId) {
      reportMemoryWriteFailure(surface, memory, 'signed_out');
      return null;
    }
    let result;
    try {
      result = await clientMemoryApi.createMemory(userId, memory);
    } catch (error) {
      reportMemoryWriteFailure(surface, memory, 'error', error?.message || error);
      return null;
    }
    if (!result) {
      // clientMemoryApi has already warned with the HTTP status or network error.
      reportMemoryWriteFailure(surface, memory, 'no_response');
      return null;
    }
    if (!result.success) {
      reportMemoryWriteFailure(surface, memory, 'not_saved', result.error);
      return result;
    }
    try {
      const updated = await clientMemoryApi.getProfile(userId);
      if (updated) setClientProfile(updated);
    } catch (error) {
      // The memory is stored; only the profile refresh after it failed.
      console.warn('Memory profile refresh failed:', error);
    }
    return result;
  }, [userId]);

  const deleteMemory = useCallback(async (memoryId) => {
    if (!userId) return;
    try {
      await clientMemoryApi.deleteMemory(userId, memoryId);
      setMemories(prev => prev.filter(m => m.id !== memoryId));
    } catch {
      // silent
    }
  }, [userId]);

  const value = {
    clientProfile,
    memories,
    isLoaded,
    getRelevant,
    syncContext: syncContextManual,
    addMemory,
    deleteMemory,
  };

  return (
    <MemoryContext.Provider value={value}>
      {children}
    </MemoryContext.Provider>
  );
};

export default MemoryContext;
